import { liveVoiceErrorMessage } from './liveVoiceErrorConfig';
/**
 * 指挥端：收到队员 voice_ptt 后按全局 FIFO 排队播报（拉取签名 URL → Audio）。
 * 同会话 media_session_id + voice_seq 按序插入；后来者不打断当前播放（仅 stopVoicePttPlayback 可停）。
 * 浏览器可能拦截无手势自动播放；失败时返回 autoplay_blocked 供 UI 提示。
 */
import { fetchTrackerMediaUrls } from '../../hooks/useTrackerMediaUrls';

let currentAudio = null;
let playing = null;
/** @type {Array<{ id: string, sessionId: string|null, voiceSeq: number|null, payload: object, arriveOrder: number }>} */
let queue = [];
let arriveSeq = 0;
let runGeneration = 0;
let draining = false;

export function isVoicePttPayload(payload) {
  if (!payload) return false;
  const t = String(payload.type || payload.kind || '').toLowerCase();
  if (t === 'voice_ptt') return true;
  const media = Array.isArray(payload.media) ? payload.media : [];
  return media.some((m) => {
    if (!m) return false;
    if (m.kind === 'audio') return true;
    return String(m.mime || '').toLowerCase().startsWith('audio/');
  });
}

/** 规范化会话字段（客户端可发 snake 或 camel） */
export function normalizeMediaSessionId(payload) {
  if (!payload) return null;
  const raw = payload.media_session_id ?? payload.mediaSessionId;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s ? s : null;
}

/** voice_seq 从 1 起；缺省或非法 → null（整段单条） */
export function normalizeVoiceSeq(payload) {
  if (!payload) return null;
  const raw = payload.voice_seq ?? payload.voiceSeq;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.floor(n);
}

function pickAudioMediaIds(payload) {
  const media = Array.isArray(payload?.media) ? payload.media : [];
  const audioOnes = media.filter((m) => {
    if (!m?.media_id) return false;
    if (m.kind === 'audio') return true;
    if (String(m.mime || '').toLowerCase().startsWith('audio/')) return true;
    return false;
  });
  if (audioOnes.length) return audioOnes.map((m) => m.media_id);
  if (String(payload?.type || '').toLowerCase() === 'voice_ptt') {
    return media.map((m) => m?.media_id).filter(Boolean);
  }
  return [];
}

function stopCurrentAudio() {
  if (!currentAudio) return;
  try {
    currentAudio.pause();
    currentAudio.src = '';
  } catch (_) { /* ignore */ }
  currentAudio = null;
}

function sameSessionSeq(a, sessionId, voiceSeq) {
  return a && a.sessionId && sessionId && a.sessionId === sessionId
    && a.voiceSeq != null && voiceSeq != null && a.voiceSeq === voiceSeq;
}

/**
 * 将条目插入队列：同 session 聚块并按 voice_seq 升序；无 session 则 FIFO 追加。
 * 已在播/已排队的同 session+seq 去重。若当前已在播更高 seq，丢弃迟到的更低 seq（不打断）。
 * @returns {{ item: object|null, skipped?: string }}
 */
export function insertVoicePttQueueItem(item, state) {
  const q = state.queue;
  const playingNow = state.playing;
  const { sessionId, voiceSeq } = item;

  if (sessionId && voiceSeq != null) {
    if (sameSessionSeq(playingNow, sessionId, voiceSeq)) {
      return { item: null, skipped: 'duplicate_playing' };
    }
    if (q.some((x) => sameSessionSeq(x, sessionId, voiceSeq))) {
      return { item: null, skipped: 'duplicate_queued' };
    }
    if (playingNow
      && playingNow.sessionId === sessionId
      && playingNow.voiceSeq != null
      && voiceSeq < playingNow.voiceSeq) {
      return { item: null, skipped: 'late_lower_seq' };
    }
  }

  if (!sessionId || voiceSeq == null) {
    q.push(item);
    return { item };
  }

  const indices = [];
  for (let i = 0; i < q.length; i += 1) {
    if (q[i].sessionId === sessionId) indices.push(i);
  }

  if (indices.length) {
    let insertAt = indices[indices.length - 1] + 1;
    for (let k = 0; k < indices.length; k += 1) {
      const i = indices[k];
      const seq = q[i].voiceSeq;
      if (seq != null && seq > voiceSeq) {
        insertAt = i;
        break;
      }
    }
    q.splice(insertAt, 0, item);
    return { item };
  }

  if (playingNow && playingNow.sessionId === sessionId) {
    let end = 0;
    while (end < q.length && q[end].sessionId === sessionId) end += 1;
    let insertAt = end;
    for (let i = 0; i < end; i += 1) {
      const seq = q[i].voiceSeq;
      if (seq != null && seq > voiceSeq) {
        insertAt = i;
        break;
      }
    }
    q.splice(insertAt, 0, item);
    return { item };
  }

  q.push(item);
  return { item };
}

function playUrls(urls, generation) {
  const playAt = (index) => new Promise((resolve) => {
    if (generation !== runGeneration) {
      resolve({ played: false, reason: 'stopped' });
      return;
    }
    if (index >= urls.length) {
      resolve({ played: true, urls });
      return;
    }
    const audio = new Audio(urls[index]);
    currentAudio = audio;
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    // playAt 必须返回 Promise；用 Promise.resolve 兜底，避免 onended 对 undefined 调 .then
    audio.onended = () => {
      Promise.resolve(playAt(index + 1)).then(finish).catch((err) => {
        finish({ played: false, reason: 'play_error', error: err, urls });
      });
    };
    audio.onerror = () => {
      Promise.resolve(playAt(index + 1)).then(finish).catch((err) => {
        finish({ played: false, reason: 'play_error', error: err, urls });
      });
    };
    Promise.resolve(audio.play()).then(() => {
      /* playing */
    }).catch((err) => {
      finish({ played: false, reason: 'autoplay_blocked', error: err, urls });
    });
  });
  return playAt(0);
}

/** @private 测试用：多 URL 链式播放（含 onended） */
export function _playUrlsForTest(urls, generation) {
  return playUrls(urls, generation == null ? runGeneration : generation);
}

async function drainQueue() {
  if (draining) return;
  draining = true;
  try {
    while (queue.length) {
      const generation = runGeneration;
      const next = queue.shift();
      playing = next;
      const ids = pickAudioMediaIds(next.payload);
      if (!ids.length) {
        playing = null;
        continue;
      }
      let items;
      try {
        items = await fetchTrackerMediaUrls(ids);
      } catch (error) {
        playing = null;
        continue;
      }
      if (generation !== runGeneration) {
        playing = null;
        break;
      }
      const urls = (items || []).map((it) => it?.url).filter(Boolean);
      if (!urls.length) {
        playing = null;
        continue;
      }
      const result = await playUrls(urls, generation);
      stopCurrentAudio();
      playing = null;
      if (generation !== runGeneration) break;
      if (result?.reason === 'autoplay_blocked') {
        queue.length = 0;
        break;
      }
    }
  } finally {
    draining = false;
    if (!queue.length) playing = null;
  }
}

/**
 * 入队并在空闲时开始播放。后来的 voice_ptt 不会 stopCurrent。
 * @returns {Promise<{ played: boolean, started?: boolean, queued?: boolean, reason?: string, skipped?: string }>}
 */
export async function playVoicePttFromPayload(payload) {
  const ids = pickAudioMediaIds(payload);
  if (!ids.length) return { played: false, reason: 'no_media' };

  const sessionId = normalizeMediaSessionId(payload);
  const voiceSeq = normalizeVoiceSeq(payload);
  const item = {
    id: 'ptt_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
    sessionId,
    voiceSeq,
    payload,
    arriveOrder: ++arriveSeq
  };

  const wasIdle = !playing && !queue.length && !draining;
  const inserted = insertVoicePttQueueItem(item, { queue, playing });
  if (!inserted.item) {
    return { played: false, reason: 'skipped', skipped: inserted.skipped };
  }

  drainQueue();
  return {
    played: true,
    started: wasIdle,
    queued: !wasIdle
  };
}

/** 用户/任务结束等显式停止：打断当前并清空队列 */
export function stopVoicePttPlayback() {
  runGeneration += 1;
  queue.length = 0;
  playing = null;
  stopCurrentAudio();
  draining = false;
}

/** @private 测试用 */
export function _getVoicePttQueueStateForTest() {
  return {
    queue: queue.map((x) => ({ id: x.id, sessionId: x.sessionId, voiceSeq: x.voiceSeq, arriveOrder: x.arriveOrder })),
    playing: playing
      ? { id: playing.id, sessionId: playing.sessionId, voiceSeq: playing.voiceSeq, arriveOrder: playing.arriveOrder }
      : null,
    draining,
    runGeneration
  };
}

/** @private 测试用 */
export function _resetVoicePttPlaybackForTest() {
  runGeneration += 1;
  queue.length = 0;
  playing = null;
  stopCurrentAudio();
  draining = false;
  arriveSeq = 0;
}

export function voicePlayErrorText(reason) {
  if (reason === 'play_error' || reason === 'autoplay_blocked') {
    return liveVoiceErrorMessage('VOICE_PLAY_FAILED');
  }
  return liveVoiceErrorMessage('VOICE_PLAY_FAILED');
}
